import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { HOST } from '../engine/anchors';
import { useTourGuide } from '../TourGuideProvider';
import { HeaderSlot } from './HeaderSlot';

const HEADER_FLOOR_MEDIA = '(min-width: 1024px)';

const HEADER_VARIANTS = ['labels', 'icons'] as const;
type HeaderVariant = (typeof HEADER_VARIANTS)[number];

const HIT_TEST_INSET = 3;

const BLOCKED_RETRY_MS = 700;

const DOCK_INSET = 20;
const DOCK_STACK_GAP = 12;
const CORNER_ZONE_PX = 200;

const DRAG_RADIUS_PX = 220;

const DRAG_THRESHOLD_PX = 6;

const DRAG_VIEWPORT_MARGIN = 8;

function useMediaMatch(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(query);
    const sync = () => setMatches((prev) => (prev === mq.matches ? prev : mq.matches));
    sync();
    if (mq.addEventListener) mq.addEventListener('change', sync);
    else mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', sync);
      else mq.removeListener(sync);
    };
  }, [query]);

  return matches;
}

function isModalBlocking(group: HTMLElement): boolean {
  return Boolean(group.closest('[data-aria-hidden="true"], [aria-hidden="true"]'));
}

function isViewportOverlay(el: Element): boolean {
  const r = el.getBoundingClientRect();
  if (r.width < window.innerWidth * 0.9 || r.height < window.innerHeight * 0.9) return false;
  const position = getComputedStyle(el).position;
  return position === 'fixed' || position === 'absolute';
}

type ProbeResult =
  | { verdict: 'fits'; variant: HeaderVariant }
  | { verdict: 'no-room' }
  | { verdict: 'blocked' };

function measureUsable(group: HTMLElement): 'usable' | 'covered' | 'blocked' {
  const rect = group.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return 'covered';
  if (rect.left < 0 || rect.right > window.innerWidth) return 'covered';

  const buttons = group.querySelectorAll<HTMLElement>('.tour-guide-launcher');
  if (buttons.length === 0) return 'covered';

  for (const button of buttons) {
    const r = button.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return 'covered';
    if (r.left < 0 || r.right > window.innerWidth) return 'covered';

    const y = r.top + r.height / 2;
    for (const x of [r.left + HIT_TEST_INSET, r.left + r.width / 2, r.right - HIT_TEST_INSET]) {
      const top = document.elementFromPoint(x, y);
      if (top && group.contains(top)) continue;
      if (top && isViewportOverlay(top)) return 'blocked';
      return 'covered';
    }
  }

  return 'usable';
}

function probeHeader(group: HTMLElement): ProbeResult {
  if (isModalBlocking(group)) return { verdict: 'blocked' };

  for (const variant of HEADER_VARIANTS) {
    group.dataset.variant = variant;
    void group.offsetWidth;
    const result = measureUsable(group);
    if (result === 'usable') return { verdict: 'fits', variant };
    if (result === 'blocked') return { verdict: 'blocked' };
  }
  return { verdict: 'no-room' };
}

function useProbeSignal(active: boolean): { signal: number; bump: () => void } {
  const [signal, setSignal] = useState(0);
  const bumpRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!active || typeof window === 'undefined') return;

    let raf = 0;
    const bump = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setSignal((n) => n + 1));
    };
    bumpRef.current = bump;

    window.addEventListener('resize', bump);

    const slot = document.querySelector(HOST.headerSlot);
    const header = slot?.closest('header') ?? null;
    const row = slot?.parentElement ?? null;

    const headerResize = header ? new ResizeObserver(bump) : null;
    headerResize?.observe(header as Element);

    const rowMutations = row
      ? new MutationObserver((records) => {
          const fromHost = records.some((r) => !slot || !slot.contains(r.target));
          if (fromHost) bump();
        })
      : null;
    rowMutations?.observe(row as Node, { childList: true, subtree: true });

    const ownRecord = (target: Node): boolean => {
      const el = target instanceof Element ? target : target.parentElement;
      if (!el) return false;
      return Boolean(el.closest('[data-tour-dock]')) || Boolean(slot?.contains(el));
    };
    const modalState = new MutationObserver((records) => {
      if (records.some((r) => !ownRecord(r.target))) bump();
    });
    modalState.observe(document.body, {
      attributes: true,
      subtree: true,
      attributeFilter: ['data-aria-hidden', 'aria-hidden'],
    });

    let cancelled = false;
    void document.fonts?.ready.then(() => {
      if (!cancelled) bump();
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', bump);
      headerResize?.disconnect();
      rowMutations?.disconnect();
      modalState.disconnect();
      bumpRef.current = () => {};
    };
  }, [active]);

  const bump = useCallback(() => bumpRef.current(), []);
  return { signal, bump };
}

const CORNER_OCCUPANTS = [HOST.headerAiLauncher, ...HOST.suppressDuringTour];

function useDockBottom(active: boolean): number {
  const [bottom, setBottom] = useState(DOCK_INSET);

  useEffect(() => {
    if (!active || typeof window === 'undefined') return;

    let raf = 0;
    const measure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const vh = window.innerHeight;
        const vw = window.innerWidth;
        let highestTop = Infinity;

        for (const selector of CORNER_OCCUPANTS) {
          document.querySelectorAll<HTMLElement>(selector).forEach((el) => {
            const r = el.getBoundingClientRect();
            if (r.width <= 0 || r.height <= 0) return;
            if (getComputedStyle(el).position !== 'fixed') return;
            if (r.bottom < vh - CORNER_ZONE_PX) return;
            if (r.right < vw - CORNER_ZONE_PX) return;
            highestTop = Math.min(highestTop, r.top);
          });
        }

        const next =
          highestTop === Infinity
            ? DOCK_INSET
            : Math.max(DOCK_INSET, Math.round(vh - highestTop + DOCK_STACK_GAP));
        setBottom((prev) => (prev === next ? prev : next));
      });
    };

    measure();
    window.addEventListener('resize', measure);
    const scrollRoot = document.getElementById('app-main-scroll');
    scrollRoot?.addEventListener('scroll', measure, { passive: true });
    const observer = new MutationObserver(measure);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'data-nexa-fab-raised'],
    });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      scrollRoot?.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [active]);

  return active ? bottom : DOCK_INSET;
}

export function ActionDock({ children }: { children: React.ReactNode }) {
  const tour = useTourGuide();
  const wideEnough = useMediaMatch(HEADER_FLOOR_MEDIA);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [pressing, setPressing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<
    { id: number; startX: number; startY: number; fromX: number; fromY: number; moved: boolean } | null
  >(null);
  const [groupEl, setGroupEl] = useState<HTMLDivElement | null>(null);

  const enabled = Boolean(tour);

  const { signal: probe, bump: reprobe } = useProbeSignal(enabled && wideEnough);

  const [tryHeader, setTryHeader] = useState(true);
  const verdictRef = useRef<'header' | 'dock'>('header');
  useLayoutEffect(() => {
    if (open || pressing) return;
    setTryHeader(wideEnough);
  }, [wideEnough, probe, open, pressing]);

  useLayoutEffect(() => {
    if (!enabled || !tryHeader || !groupEl) return;
    const result = probeHeader(groupEl);

    if (result.verdict === 'blocked') {
      if (verdictRef.current === 'dock') setTryHeader(false);
      const retry = window.setTimeout(reprobe, BLOCKED_RETRY_MS);
      return () => window.clearTimeout(retry);
    }

    if (result.verdict === 'no-room') {
      verdictRef.current = 'dock';
      setTryHeader(false);
      return;
    }

    verdictRef.current = 'header';
    delete groupEl.dataset.probing;
  }, [enabled, tryHeader, probe, groupEl, children, reprobe]);

  const dockable = enabled && !tryHeader;

  const running = Boolean(tour?.run.running || tour?.task.running);
  const dockBottom = useDockBottom(dockable);

  const [engaged, setEngaged] = useState(false);
  useEffect(() => {
    if (open) setEngaged(true);
  }, [open]);

  const wasRunningRef = useRef(false);
  useEffect(() => {
    if (running) {
      wasRunningRef.current = true;
      return;
    }
    if (!wasRunningRef.current) return;
    wasRunningRef.current = false;
    setEngaged(false);
  }, [running]);
  const attention = enabled && !engaged && !open && !running && !pressing;

  const close = useCallback(() => setOpen(false), []);


  const clampOffset = useCallback((next: { x: number; y: number }) => {
    let { x, y } = next;

    const root = rootRef.current;
    if (root) {
      const rect = root.getBoundingClientRect();
      const homeLeft = rect.left - offset.x;
      const homeRight = rect.right - offset.x;
      const homeTop = rect.top - offset.y;
      const homeBottom = rect.bottom - offset.y;
      const m = DRAG_VIEWPORT_MARGIN;
      x = Math.min(Math.max(x, m - homeLeft), window.innerWidth - m - homeRight);
      y = Math.min(Math.max(y, m - homeTop), window.innerHeight - m - homeBottom);
    }

    const distance = Math.hypot(x, y);
    if (distance > DRAG_RADIUS_PX) {
      const scale = DRAG_RADIUS_PX / distance;
      x *= scale;
      y *= scale;
    }
    return { x: Math.round(x), y: Math.round(y) };
  }, [offset.x, offset.y]);

  const onFabPointerDown = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;
    setPressing(true);
    dragRef.current = {
      id: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      fromX: offset.x,
      fromY: offset.y,
      moved: false,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [offset.x, offset.y]);

  const onFabPointerMove = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.id !== event.pointerId) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    if (!drag.moved) {
      drag.moved = true;
      setDragging(true);
    }
    setOffset(clampOffset({ x: drag.fromX + dx, y: drag.fromY + dy }));
  }, [clampOffset]);

  const onFabPointerUp = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.id !== event.pointerId) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    if (!drag.moved) setOpen((prev) => !prev);
    setPressing(false);
    setDragging(false);
  }, []);

  useEffect(() => {
    const reclamp = () => setOffset((prev) => {
      const next = clampOffset(prev);
      return next.x === prev.x && next.y === prev.y ? prev : next;
    });
    reclamp();
    window.addEventListener('resize', reclamp);
    return () => window.removeEventListener('resize', reclamp);
  }, [open, dockBottom, clampOffset]);

  useEffect(() => {
    if (running) setOffset({ x: 0, y: 0 });
  }, [running]);

  useEffect(() => {
    if (running) close();
  }, [running, close]);

  useEffect(() => {
    if (dockable) return;
    const settle = window.setTimeout(close, 120);
    return () => window.clearTimeout(settle);
  }, [dockable, close]);

  useEffect(() => {
    if (!dockable || !open) return;

    const onPointerDown = (event: Event) => {
      const target = event.target as Node | null;
      if (target && rootRef.current && !rootRef.current.contains(target)) close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [dockable, open, close]);

  if (!dockable) {
    return (
      <HeaderSlot>
        <div
          ref={setGroupEl}
          className="tour-guide-actions tour-guide-actions--header"
          data-testid="tour-guide-actions"
          data-probing={enabled ? '' : undefined}
          data-attention={attention ? '' : undefined}
        >
          {children}
        </div>
      </HeaderSlot>
    );
  }

  return createPortal(
    <div
      ref={rootRef}
      className="tour-guide-dock"
      data-tour-dock=""
      data-open={open ? '' : undefined}
      data-attention={attention ? '' : undefined}
      data-hidden={running ? '' : undefined}
      data-dragging={dragging ? '' : undefined}
      style={{
        bottom: dockBottom,
        transform: offset.x || offset.y ? `translate(${offset.x}px, ${offset.y}px)` : undefined,
      }}
    >
      <div
        id="tour-guide-dock-menu"
        className="tour-guide-actions tour-guide-actions--dock"
        data-testid="tour-guide-actions"
        role="group"
        aria-label="Explore the WeNext sandbox"
        aria-hidden={open ? undefined : true}
        onClick={close}
      >
        <span className="tour-guide-dock__title">Explore the sandbox</span>
        {children}
      </div>

      <button
        type="button"
        className="tour-guide-dock__fab"
        data-testid="tour-guide-dock-toggle"
        aria-expanded={open}
        aria-controls="tour-guide-dock-menu"
        aria-label={open ? 'Close sandbox actions' : 'Sandbox actions: quick tour, plans, demo'}
        onPointerDown={onFabPointerDown}
        onPointerMove={onFabPointerMove}
        onPointerUp={onFabPointerUp}
        onPointerCancel={onFabPointerUp}
        onClick={(event) => {
          if (event.detail !== 0) return;
          setOpen((prev) => !prev);
        }}
      >
        <span className="tour-guide-dock__fab-label" aria-hidden="true">
          {open ? 'Close' : 'Quick tour, plans and demo'}
        </span>
        {open ? (
          <svg
            className="tour-guide-dock__fab-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg
            className="tour-guide-dock__fab-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
          </svg>
        )}
      </button>
    </div>,
    document.body,
  );
}
